import { Box, Text } from '@csscompany/csds-components';
import {
  FacilityDetailsPage,
  parseFacilityData,
  rosetta,
} from '@csscompany/op-facility-finder';
import { isBrowser } from '@publicss/utils';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Meta } from '../components/Meta';
import SliceZone from '../components/SliceZone';
import { LeadForm } from '../components/kitchen-growth/LeadForm/LeadForm';
import { LoadingState } from '../components/kitchen-growth/LoadingState';
import { MobileCopySave } from '../components/kitchen-growth/MobileCopySave';
import { Button } from '../components/ui';
import {
  BM_SCROLL_CONTAINER_ID,
  DETAIL_NAV_HOLDER_ID,
  DETAIL_NEAR_BY_ID,
  DETAIL_LEAD_FORM_ID,
  LOG_PRODUCT_NAME,
  BOOK_TOUR_COMPONENT,
  LOCATIONS_DETAILS_COMPONENT_NAME,
} from '../constants/facilityConstants';
import { Schemas } from '../seo/Schemas';
import { useIsMobile, useIsDesktop } from '../utils/breakpoints';
import { useMixpanel } from '../utils/mixpanel';

const StyledPage = styled.div`
  [data-testid='csds-text'] {
    p {
      margin-top: 4px;
      margin-bottom: 0px;
    }
  }
`;

const getLocalBusinessSchema = (facility) =>
  JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: facility?.bs_name ?? '',
      image: facility?.bs_image ?? '',
      url: facility?.bs_url ?? '',
      telephone: facility?.bs_telephone ?? '',
      address: {
        '@type': 'PostalAddress',
        streetAddress: facility?.bs_street_address ?? '',
        addressLocality: facility?.bs_locality ?? '',
        addressRegion: facility?.bs_region ?? '',
        postalCode: facility?.bs_postal ?? '',
        addressCountry: facility?.bs_address_country ?? '',
      },
    },
    null,
    2
  );

export const Head = ({ data, pageContext, location: { pathname } }) => {
  const facility = data.prismicFacilityGroup.data.facilities[0];
  const { siteURL } = pageContext;
  const localBusinessSchema = facility
    ? getLocalBusinessSchema(facility)
    : null;

  return (
    <>
      <Meta
        pageContext={pageContext}
        pageTitle={facility.meta_title?.text ?? ''}
        title={facility.meta_title?.text ?? ''}
        description={facility.meta_description?.text ?? ''}
        url={`${siteURL}${pathname}`}
      />
      <Schemas data={data} pageContext={pageContext} />
      {localBusinessSchema && (
        <script type="application/ld+json">{localBusinessSchema}</script>
      )}
    </>
  );
};

function FacilityDetails({ data, pageContext, location }) {
  const ffVariablesData = data?.prismicFacilityFinderVariables?.data;
  const allData = data.allPrismicFacilityGroup.edges
    .filter(({ node }) => node.data.locale === pageContext.siteLang)
    .map(({ node }) => ({
      ...node,
      data: {
        ...node.data,
        facility_tags: node.data.facility_tags.map(({ label }) => ({
          label: [label],
        })),
      },
    }));
  const prefetchedData = parseFacilityData(allData);
  // TODO: Pass the raw query to FacilityDetailsPage as initialData for useQuery
  const facility = data.prismicFacilityGroup.data.facilities[0];
  const facilityDemographics = pageContext.demographicsData
    ? {
        currencyCode: pageContext.demographicsData.currencyCode,
        countryCode: pageContext.demographicsData.countryCode,
        values: pageContext.demographicsData.demographicsLines
          // TODO: Add logic to pick the "best" mode to display
          // for now just stick to using car 15 min driving distance
          .filter((line) => line.area === 'AREA_DISTANCE_CAR_15MIN')
          .reduce((map, line) => {
            map.set(line.label, line.value);
            return map;
          }, new Map()),
      }
    : undefined;

  const mixpanelClient = useMixpanel();
  const allDemographicsInfo = data.allPrismicDemographicsinfo.edges
    .filter(({ node }) => node.data.locale === pageContext.siteLang)
    .map((item) => {
      return {
        label: item.node.data.label,
        title: item.node.data.title.text,
        description: item.node.data.description.text,
      };
    });

  useEffect(() => {
    mixpanelClient.track({
      product: LOG_PRODUCT_NAME,
      component: LOCATIONS_DETAILS_COMPONENT_NAME,
      event: 'Page Landed',
    });

    const scrollContainer = document.body;
    const scroll = () => {
      if (scrollContainer) {
        const detailNav = document.getElementById(DETAIL_NAV_HOLDER_ID);
        const leadForm = document.getElementById(DETAIL_LEAD_FORM_ID);
        const nearBy = document.getElementById(DETAIL_NEAR_BY_ID);

        if (detailNav && leadForm) {
          const top = detailNav.offsetHeight + 68;
          const formTopPosition = leadForm.getBoundingClientRect().top;
          const formChildTopPosition =
            leadForm.childNodes[0].getBoundingClientRect().top;
          const formBottomPosition =
            leadForm.childNodes[0].getBoundingClientRect().bottom;
          const bottom = nearBy.getBoundingClientRect().top;

          if (formTopPosition <= top && bottom >= formBottomPosition + 44) {
            leadForm.childNodes[0].style['margin-top'] = `${
              (formTopPosition - top) * -1
            }px`;
          } else if (
            formChildTopPosition > top &&
            bottom <= formBottomPosition + 44
          ) {
            leadForm.childNodes[0].style['margin-top'] = `${
              (formTopPosition - top) * -1
            }px`;
          }
        }
      }
    };
    if (isBrowser()) {
      window?.addEventListener('scroll', () => {
        scroll();
      });
    }

    return () => {
      if (isBrowser()) {
        window?.removeEventListener('scroll', scroll);
      }
    };
  }, []);

  function mixpanel360Clicked() {
    mixpanelClient.track({
      product: LOG_PRODUCT_NAME,
      component: LOCATIONS_DETAILS_COMPONENT_NAME,
      event: 'click 360',
    });
  }

  function mixpanelDetailsBookTourClicked(position) {
    mixpanelClient.track(
      {
        product: LOG_PRODUCT_NAME,
        component: BOOK_TOUR_COMPONENT,
        event: 'click',
      },
      {
        position: `Details page - ${position}`,
      }
    );
  }

  function mixpanelD2cUrlClicked() {
    mixpanelClient.track(
      {
        product: LOG_PRODUCT_NAME,
        component: LOCATIONS_DETAILS_COMPONENT_NAME,
        event: 'Visit D2C URL',
      },
      {
        brand_name: facility.brand_name?.text,
        city: facility.city?.text,
        d2c_url: facility.d2c_url?.url,
      }
    );
  }

  return (
    <StyledPage className="min-h-screen">
      {facility ? (
        <Box
          background="var(--grey-2)"
          display="flex"
          flexDirection="column"
          alignItems="center"
          flexShrink={0}
          flexGrow={1}>
          <Box
            width="100%"
            maxWidth={{
              _: '1440px',
              $12: '1920px',
            }}
            display="grid"
            gap="$4"
            flexGrow={1}>
            <FacilityDetailsPage
              useIsMobile={useIsMobile}
              useIsDesktop={useIsDesktop}
              prefetchedData={prefetchedData}
              allDemographicsInfo={allDemographicsInfo}
              prefetchedFacilityDemographics={facilityDemographics}
              BM_SCROLL_CONTAINER_ID={BM_SCROLL_CONTAINER_ID}
              LeadForm={LeadForm}
              TypeformButton={
                <Button globalTypeform utm_content="ff-map-cta" size="sm">
                  <Text variant="$labelSmallBold">
                    {rosetta.facility_card_book_tour_button.getText()}
                  </Text>
                </Button>
              }
              MobileCopySave={MobileCopySave}
              mixpanel={{
                clicked360: mixpanel360Clicked,
                bookTourClicked: mixpanelDetailsBookTourClicked,
                d2cUrlClicked: mixpanelD2cUrlClicked,
              }}
            />
          </Box>
          {ffVariablesData && (
            <Box className="w-full">
              <SliceZone
                slices={ffVariablesData.body}
                pageContext={pageContext}
                slug={facility.slug}
                location={location}
              />
            </Box>
          )}
        </Box>
      ) : (
        <LoadingState />
      )}
    </StyledPage>
  );
}

export default withPrismicPreview(FacilityDetails);

export const query = graphql`
  query Facilities($slug: String!, $siteTag: String!, $siteLang: String!) {
    allPrismicFacilityGroup(filter: { tags: { eq: $siteTag } }) {
      edges {
        node {
          _previewable
          data {
            locale
            facilities {
              bs_name
              bs_url
              bs_telephone
              bs_street_address
              bs_same_as
              bs_region
              bs_postal
              bs_opens
              bs_locality
              bs_image
              bs_id
              bs_day_of_week
              bs_closes
              bs_address_country
              slug
              sfdc
              id
              address {
                richText
                text
              }
              brand_name {
                richText
                text
              }
              calendly {
                url
              }
              city {
                richText
                text
              }
              country {
                richText
                text
              }
              dine_in {
                richText
                text
              }
              d2c_url {
                url
              }
              facility_description {
                richText
                text
              }
              facility_headline {
                richText
                text
              }
              kitchen_size_range {
                richText
                text
              }
              latitude {
                richText
                text
              }
              license_fee {
                richText
                text
              }
              longitude {
                richText
                text
              }
              onsite_parking {
                richText
                text
              }
              renders {
                document {
                  ... on PrismicFacilityImages {
                    id
                    _previewable
                    data {
                      images {
                        image_url {
                          url
                        }
                      }
                    }
                  }
                }
              }
              state {
                richText
              }
              demographics_selection_1 {
                document {
                  ... on PrismicDemographicsinfo {
                    _previewable
                    data {
                      label
                      title {
                        text
                        richText
                      }
                      description {
                        text
                        richText
                      }
                    }
                  }
                }
              }
              demographics_selection_2 {
                document {
                  ... on PrismicDemographicsinfo {
                    _previewable
                    data {
                      label
                      title {
                        text
                        richText
                      }
                      description {
                        text
                        richText
                      }
                    }
                  }
                }
              }
              demographics_selection_3 {
                document {
                  ... on PrismicDemographicsinfo {
                    _previewable
                    data {
                      label
                      title {
                        text
                        richText
                      }
                      description {
                        text
                        richText
                      }
                    }
                  }
                }
              }
              demographics_selection_4 {
                document {
                  ... on PrismicDemographicsinfo {
                    _previewable
                    data {
                      label
                      title {
                        text
                        richText
                      }
                      description {
                        text
                        richText
                      }
                    }
                  }
                }
              }
            }
            demographics {
              demographics_info {
                document {
                  ... on PrismicDemographicsinfo {
                    _previewable
                    data {
                      label
                      title {
                        text
                        richText
                      }
                      description {
                        text
                        richText
                      }
                    }
                  }
                }
              }
            }
            facility_tags {
              label {
                text
              }
            }
            kitchen_layout_module {
              document {
                ... on PrismicKitchenLayoutModule {
                  id
                  _previewable
                  data {
                    additional_storage {
                      richText
                    }
                    amenity_list {
                      amenity_icon {
                        url
                      }
                      description {
                        richText
                      }
                      item {
                        richText
                      }
                    }
                    compare_after {
                      image {
                        url
                      }
                    }
                    compare_before {
                      url
                    }
                    equipment_provided {
                      item {
                        richText
                      }
                    }
                    infrastructure_icon {
                      url
                    }
                    infrastructure_list {
                      list_detail {
                        richText
                      }
                    }
                    infrastructure_title {
                      richText
                    }
                    services_icon {
                      url
                    }
                    services_list {
                      list_detail {
                        richText
                      }
                    }
                    services_title {
                      richText
                    }
                    space_amenities_title {
                      richText
                    }
                    space_icon {
                      url
                    }
                    space_list {
                      list_detail {
                        richText
                      }
                    }
                    utility_icon {
                      url
                    }
                    utility_list {
                      list_detail {
                        richText
                      }
                    }
                    utility_title {
                      richText
                    }
                  }
                }
              }
            }
          }
          tags
        }
      }
    }

    prismicFacilityGroup(
      data: {
        facilities: { elemMatch: { slug: { eq: $slug } } }
        locale: { eq: $siteLang }
      }
    ) {
      _previewable
      data {
        locale
        facilities {
          id
          bs_name
          bs_url
          bs_telephone
          bs_street_address
          bs_same_as
          bs_region
          bs_postal
          bs_opens
          bs_locality
          bs_image
          bs_id
          bs_day_of_week
          bs_closes
          bs_address_country
          city {
            text
          }
          d2c_url {
            url
          }
          slug
          meta_title {
            text
          }
          meta_description {
            text
          }
          brand_name {
            text
          }
        }
        facility_tags {
          label {
            text
          }
        }
      }
    }

    allPrismicDemographicsinfo(filter: { tags: { eq: $siteTag } }) {
      edges {
        node {
          _previewable
          data {
            locale
            label
            title {
              text
            }
            description {
              text
            }
          }
        }
      }
    }

    prismicFacilityFinderVariables(
      tags: { eq: $siteTag }
      data: { locale: { eq: $siteLang } }
    ) {
      _previewable
      data {
        locale
        body {
          ... on PrismicFacilityFinderVariablesDataBodyBlockScrollable {
            slice_type
            primary {
              variant
              eyebrow {
                text
                html
                richText
              }
              headline {
                text
                html
                richText
              }
              group_1_label
              group_2_label
              group_3_label
            }
            items {
              group
              label
              link {
                url
              }
            }
          }
        }
      }
    }
  }
`;
